import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { findFuzzyColumnIndex } from "@/lib/table-utils";

const PREVIEW_ROW_LIMIT = 5;

interface GroupedRowMeta {
  groupId: string;
  groupName: string;
}

interface RowSelectionTableProps {
  tableId: string;
  headers: string[];
  rows: string[][];
  selectedRows: Set<number>;
  groupedRowsLookup: Map<string, GroupedRowMeta>;
  onRowToggle: (tableId: string, rowIndex: number, selected: boolean) => void;
}

export function RowSelectionTable({
  tableId,
  headers,
  rows,
  selectedRows,
  groupedRowsLookup,
  onRowToggle,
}: RowSelectionTableProps) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [showAllRows, setShowAllRows] = useState(false);
  
  const titleIndex = findFuzzyColumnIndex(headers, ["title", "code", "item", "mark", "id"]);
  const descriptionIndex = findFuzzyColumnIndex(headers, ["description", "desc", "type", "model"]);

  const selectedRow = selectedRowIndex !== null ? rows[selectedRowIndex] : null;
  const selectedRowGroup = selectedRowIndex !== null 
    ? groupedRowsLookup.get(`${tableId}:${selectedRowIndex}`)
    : null;
  const displayedRows = showAllRows ? rows : rows.slice(0, PREVIEW_ROW_LIMIT);
  const hiddenRowCount = Math.max(rows.length - PREVIEW_ROW_LIMIT, 0);

  return (
    <>
      <div className="mt-3 rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/70 border-b-2 border-border">
            <tr>
              <th className="w-10 px-2 py-2 text-left font-bold uppercase text-[10px] tracking-wider">Add</th>
              <th className="px-2 py-2 text-left font-bold uppercase text-[10px] tracking-wider max-w-[200px]">Item</th>
              <th className="hidden md:table-cell px-2 py-2 text-left font-bold uppercase text-[10px] tracking-wider max-w-[300px]">Details</th>
              <th className="w-[180px] px-2 py-2 text-left font-bold uppercase text-[10px] tracking-wider">Merge Rows</th>
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row, rowIndex) => {
              const key = `${tableId}:${rowIndex}`;
              const groupedMeta = groupedRowsLookup.get(key);
              const title = row[titleIndex] || `Row ${rowIndex + 1}`;
              const detail = row[descriptionIndex] || "";

              return (
                <tr 
                  key={key} 
                  className="border-b border-border transition-colors hover:bg-muted/50 cursor-pointer"
                  onClick={() => setSelectedRowIndex(rowIndex)}
                >
                  <td className="px-2 py-1.5 align-top" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRows.has(rowIndex)}
                      disabled={Boolean(groupedMeta)}
                      onCheckedChange={(checked) =>
                        onRowToggle(tableId, rowIndex, checked === true)
                      }
                      aria-label={`Select row ${rowIndex + 1}`}
                      title={
                        groupedMeta
                          ? `Already merged in ${groupedMeta.groupName}`
                          : "Select row to merge"
                      }
                    />
                  </td>
                  <td className="px-2 py-1.5 max-w-[200px] text-[11px] leading-tight whitespace-normal break-words align-top font-medium">
                    {title}
                  </td>
                  <td className="hidden md:table-cell px-2 py-1.5 max-w-[300px] text-[11px] leading-tight whitespace-normal break-words align-top text-muted-foreground">
                    {detail || "-"}
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    {groupedMeta ? (
                      <Badge variant="secondary" title={groupedMeta.groupName} className="text-[10px] leading-tight whitespace-normal break-words max-w-[160px]">
                        {groupedMeta.groupName}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">Not merged</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!showAllRows && hiddenRowCount > 0 && (
          <div className="border-t border-border bg-muted/30 p-2 text-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAllRows(true)}
            >
              Show all {rows.length} rows
            </Button>
          </div>
        )}
      </div>

      {/* Row Detail Modal */}
      <Dialog 
        open={selectedRowIndex !== null} 
        onOpenChange={(open) => !open && setSelectedRowIndex(null)}
      >
        <DialogContent className="w-[90vw] max-w-5xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b-2 border-border">
            <DialogTitle className="flex items-center gap-3">
              Row Details
              {selectedRowGroup && (
                <Badge variant="secondary">{selectedRowGroup.groupName}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedRowIndex !== null && `Row ${selectedRowIndex + 1} of ${rows.length}`}
            </DialogDescription>
          </DialogHeader>

          {selectedRow && (
            <div className="p-6 space-y-4">
              {headers.map((header, colIndex) => {
                const value = selectedRow[colIndex];
                if (!value?.trim()) return null;
                
                return (
                  <div key={colIndex} className="border-b border-border pb-3 last:border-0">
                    <span className="font-bold text-muted-foreground text-xs uppercase tracking-wider block mb-1.5">
                      {header}
                    </span>
                    <span className="text-foreground font-medium text-base">
                      {value}
                    </span>
                  </div>
                );
              })}
              {selectedRow.every((cell) => !cell?.trim()) && (
                <p className="text-center text-muted-foreground py-8">No data in this row</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
